import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, TrashIcon, EditIcon, CheckIcon, XIcon, AlertTriangleIcon, LockIcon, UnlockIcon, FilterIcon, RefreshCwIcon } from 'lucide-react';
export function StatusManagement({
  locale = 'en-US'
}) {
  // Sample status data - in a real app, this would come from an API
  const [statuses, setStatuses] = useState([{
    id: 'status-001',
    label: 'Draft',
    color: '#E5E7EB',
    isSystem: true,
    category: 'document',
    usageCount: 452
  }, {
    id: 'status-002',
    label: 'Pending Approval',
    color: '#FEF3C7',
    isSystem: true,
    category: 'workflow',
    usageCount: 128
  }, {
    id: 'status-003',
    label: 'Approved',
    color: '#D1FAE5',
    isSystem: true,
    category: 'workflow',
    usageCount: 356
  }, {
    id: 'status-004',
    label: 'Rejected',
    color: '#FEE2E2',
    isSystem: true,
    category: 'workflow',
    usageCount: 73
  }, {
    id: 'status-005',
    label: 'Completed',
    color: '#DBEAFE',
    isSystem: true,
    category: 'document',
    usageCount: 289
  }, {
    id: 'status-006',
    label: 'On Hold',
    color: '#E0E7FF',
    isSystem: false,
    category: 'custom',
    usageCount: 42
  }, {
    id: 'status-007',
    label: 'Expired',
    color: '#FCE7F3',
    isSystem: false,
    category: 'document',
    usageCount: 18
  }, {
    id: 'status-008',
    label: 'In Progress',
    color: '#FEF3C7',
    isSystem: true,
    category: 'workflow',
    usageCount: 205
  }, {
    id: 'status-009',
    label: 'Cancelled',
    color: '#F3F4F6',
    isSystem: true,
    category: 'document',
    usageCount: 97
  }, {
    id: 'status-010',
    label: 'Needs Attention',
    color: '#FEE2E2',
    isSystem: false,
    category: 'custom',
    usageCount: 31
  }, {
    id: 'status-011',
    label: 'Awaiting Payment',
    color: '#FEF3C7',
    isSystem: true,
    category: 'financial',
    usageCount: 184
  }, {
    id: 'status-012',
    label: 'Paid',
    color: '#D1FAE5',
    isSystem: true,
    category: 'financial',
    usageCount: 263
  }, {
    id: 'status-013',
    label: 'Partially Paid',
    color: '#DBEAFE',
    isSystem: true,
    category: 'financial',
    usageCount: 87
  }, {
    id: 'status-014',
    label: 'Under Review',
    color: '#E0E7FF',
    isSystem: false,
    category: 'custom',
    usageCount: 56
  }]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingStatus, setEditingStatus] = useState(null);
  const [newStatus, setNewStatus] = useState({
    label: '',
    color: '#E5E7EB',
    category: 'custom'
  });
  const [showNewForm, setShowNewForm] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [systemFilter, setSystemFilter] = useState('all');
  const [sortBy, setSortBy] = useState('label');
  const [sortDirection, setSortDirection] = useState('asc');
  // Available status categories
  const categories = [{
    id: 'all',
    name: 'All Categories'
  }, {
    id: 'document',
    name: 'Document'
  }, {
    id: 'workflow',
    name: 'Workflow'
  }, {
    id: 'financial',
    name: 'Financial'
  }, {
    id: 'custom',
    name: 'Custom'
  }];
  // Filter and sort statuses
  const filteredStatuses = statuses.filter(status => {
    // Apply search filter
    const matchesSearch = status.label.toLowerCase().includes(searchTerm.toLowerCase());
    // Apply category filter
    const matchesCategory = categoryFilter === 'all' || status.category === categoryFilter;
    // Apply system/custom filter
    const matchesSystemFilter = systemFilter === 'all' || systemFilter === 'system' && status.isSystem || systemFilter === 'custom' && !status.isSystem;
    return matchesSearch && matchesCategory && matchesSystemFilter;
  }).sort((a, b) => {
    // Apply sorting
    if (sortBy === 'label') {
      return sortDirection === 'asc' ? a.label.localeCompare(b.label) : b.label.localeCompare(a.label);
    } else if (sortBy === 'usage') {
      return sortDirection === 'asc' ? a.usageCount - b.usageCount : b.usageCount - a.usageCount;
    }
    return 0;
  });
  // Handle editing a status
  const startEditing = status => {
    setEditingStatus({
      ...status
    });
  };
  const cancelEditing = () => {
    setEditingStatus(null);
  };
  const saveEditedStatus = () => {
    if (!editingStatus.label.trim()) {
      alert('Status label cannot be empty');
      return;
    }
    setStatuses(statuses.map(status => status.id === editingStatus.id ? {
      ...status,
      label: editingStatus.label,
      color: editingStatus.color,
      category: editingStatus.isSystem ? status.category : editingStatus.category
    } : status));
    setEditingStatus(null);
  };
  // Handle creating a new status
  const handleCreateStatus = () => {
    if (!newStatus.label.trim()) {
      alert('Status label cannot be empty');
      return;
    }
    const newId = `status-${String(statuses.length + 1).padStart(3, '0')}`;
    setStatuses([...statuses, {
      id: newId,
      label: newStatus.label,
      color: newStatus.color,
      isSystem: false,
      category: newStatus.category,
      usageCount: 0
    }]);
    setNewStatus({
      label: '',
      color: '#E5E7EB',
      category: 'custom'
    });
    setShowNewForm(false);
  };
  // Handle deleting a status
  const handleDeleteStatus = id => {
    const statusToDelete = statuses.find(status => status.id === id);
    if (statusToDelete.isSystem) {
      alert('System statuses cannot be deleted');
      return;
    }
    if (statusToDelete.usageCount > 0) {
      const confirmDelete = window.confirm(`This status is used in ${statusToDelete.usageCount} places. Are you sure you want to delete it?`);
      if (!confirmDelete) return;
    }
    setStatuses(statuses.filter(status => status.id !== id));
  };
  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setSystemFilter('all');
    setSortBy('label');
    setSortDirection('asc');
  };
  // Toggle sort direction
  const toggleSort = field => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };
  return <div data-id="element-20367">
      <div className="mb-8" data-id="element-20368">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-20369">Status Management</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-20370">
          Manage statuses used throughout the system
        </p>
      </div>
      {/* Search and Filter Bar */}
      <div className="bg-white shadow rounded-lg mb-6" data-id="element-20371">
        <div className="p-4 border-b border-gray-200" data-id="element-20372">
          <div className="flex flex-col sm:flex-row justify-between space-y-3 sm:space-y-0 sm:space-x-4" data-id="element-20373">
            <div className="relative flex-grow" data-id="element-20374">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-20375">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-20376" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search statuses..." data-id="element-20377" />
            </div>
            <div className="flex space-x-2" data-id="element-20378">
              <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-20379">
                {categories.map(category => <option key={category.id} value={category.id} data-id="element-20380">
                    {category.name}
                  </option>)}
              </select>
              <select value={systemFilter} onChange={e => setSystemFilter(e.target.value)} className="block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-20381">
                <option value="all" data-id="element-20382">All Statuses</option>
                <option value="system" data-id="element-20383">System Only</option>
                <option value="custom" data-id="element-20384">Custom Only</option>
              </select>
              <button type="button" onClick={resetFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20385">
                <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-20386" />
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Status List */}
      <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-20387">
        <div className="flex justify-between items-center p-4 border-b border-gray-200" data-id="element-20388">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-20389">Statuses</h3>
          <button type="button" onClick={() => setShowNewForm(!showNewForm)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20390">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-20391" />
            Add Status
          </button>
        </div>
        {/* New Status Form */}
        {showNewForm && <div className="p-4 bg-gray-50 border-b border-gray-200" data-id="element-20392">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-20393">
              <div data-id="element-20394">
                <label htmlFor="new-status-label" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20395">
                  Status Label
                </label>
                <input type="text" id="new-status-label" value={newStatus.label} onChange={e => setNewStatus({
              ...newStatus,
              label: e.target.value
            })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Enter status label" data-id="element-20396" />
              </div>
              <div data-id="element-20397">
                <label htmlFor="new-status-color" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20398">
                  Color
                </label>
                <div className="flex items-center" data-id="element-20399">
                  <input type="color" id="new-status-color" value={newStatus.color} onChange={e => setNewStatus({
                ...newStatus,
                color: e.target.value
              })} className="h-8 w-8 border-gray-300 rounded-md shadow-sm mr-2" data-id="element-20400" />
                  <input type="text" value={newStatus.color} onChange={e => setNewStatus({
                ...newStatus,
                color: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-20401" />
                </div>
              </div>
              <div data-id="element-20402">
                <label htmlFor="new-status-category" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20403">
                  Category
                </label>
                <select id="new-status-category" value={newStatus.category} onChange={e => setNewStatus({
              ...newStatus,
              category: e.target.value
            })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-20404">
                  <option value="document" data-id="element-20405">Document</option>
                  <option value="workflow" data-id="element-20406">Workflow</option>
                  <option value="financial" data-id="element-20407">Financial</option>
                  <option value="custom" data-id="element-20408">Custom</option>
                </select>
              </div>
              <div className="flex items-end space-x-2" data-id="element-20409">
                <button type="button" onClick={handleCreateStatus} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20410">
                  <CheckIcon className="h-4 w-4 mr-1" data-id="element-20411" />
                  Save
                </button>
                <button type="button" onClick={() => setShowNewForm(false)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20412">
                  <XIcon className="h-4 w-4 mr-1" data-id="element-20413" />
                  Cancel
                </button>
              </div>
            </div>
          </div>}
        {/* Status Table */}
        <div className="overflow-x-auto" data-id="element-20414">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-20415">
            <thead className="bg-gray-50" data-id="element-20416">
              <tr data-id="element-20417">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('label')} data-id="element-20418">
                  <div className="flex items-center" data-id="element-20419">
                    Status
                    {sortBy === 'label' && <span className="ml-1" data-id="element-20420">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20421">
                  Preview
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20422">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20423">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('usage')} data-id="element-20424">
                  <div className="flex items-center" data-id="element-20425">
                    Usage
                    {sortBy === 'usage' && <span className="ml-1" data-id="element-20426">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20427">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-20428">
              {filteredStatuses.length === 0 ? <tr data-id="element-20429">
                  <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-20430">
                    No statuses found matching your filters
                  </td>
                </tr> : filteredStatuses.map(status => <tr key={status.id} data-id="element-20431">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-20432">
                      {editingStatus && editingStatus.id === status.id ? <input type="text" value={editingStatus.label} onChange={e => setEditingStatus({
                  ...editingStatus,
                  label: e.target.value
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-20433" /> : <div className="text-sm font-medium text-gray-900" data-id="element-20434">
                          {status.label}
                        </div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-20435">
                      {editingStatus && editingStatus.id === status.id ? <div className="flex items-center" data-id="element-20436">
                          <input type="color" value={editingStatus.color} onChange={e => setEditingStatus({
                    ...editingStatus,
                    color: e.target.value
                  })} className="h-8 w-8 border-gray-300 rounded-md shadow-sm mr-2" data-id="element-20437" />
                          <input type="text" value={editingStatus.color} onChange={e => setEditingStatus({
                    ...editingStatus,
                    color: e.target.value
                  })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-24 sm:text-sm border-gray-300 rounded-md" data-id="element-20438" />
                        </div> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{
                  backgroundColor: status.color,
                  color: getContrastColor(status.color)
                }} data-id="element-20439">
                          {status.label}
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-20440">
                      {editingStatus && editingStatus.id === status.id && !status.isSystem ? <select value={editingStatus.category} onChange={e => setEditingStatus({
                  ...editingStatus,
                  category: e.target.value
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-20441">
                          <option value="document" data-id="element-20442">Document</option>
                          <option value="workflow" data-id="element-20443">Workflow</option>
                          <option value="financial" data-id="element-20444">Financial</option>
                          <option value="custom" data-id="element-20445">Custom</option>
                        </select> : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800" data-id="element-20446">
                          {categories.find(c => c.id === status.category)?.name || status.category}
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-20447">
                      <div className="flex items-center" data-id="element-20448">
                        {status.isSystem ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-20449">
                            <LockIcon className="h-3 w-3 mr-1" data-id="element-20450" />
                            System
                          </span> : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-20451">
                            <UnlockIcon className="h-3 w-3 mr-1" data-id="element-20452" />
                            Custom
                          </span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-20453">
                      {status.usageCount}{' '}
                      {status.usageCount === 1 ? 'use' : 'uses'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-20454">
                      {editingStatus && editingStatus.id === status.id ? <div className="flex space-x-2 justify-end" data-id="element-20455">
                          <button onClick={saveEditedStatus} className="text-indigo-600 hover:text-indigo-900" data-id="element-20456">
                            <CheckIcon className="h-5 w-5" data-id="element-20457" />
                          </button>
                          <button onClick={cancelEditing} className="text-gray-600 hover:text-gray-900" data-id="element-20458">
                            <XIcon className="h-5 w-5" data-id="element-20459" />
                          </button>
                        </div> : <div className="flex space-x-2 justify-end" data-id="element-20460">
                          <button onClick={() => startEditing(status)} className="text-indigo-600 hover:text-indigo-900" data-id="element-20461">
                            <EditIcon className="h-5 w-5" data-id="element-20462" />
                          </button>
                          {!status.isSystem && <button onClick={() => handleDeleteStatus(status.id)} className="text-red-600 hover:text-red-900" disabled={status.isSystem} data-id="element-20463">
                              <TrashIcon className="h-5 w-5" data-id="element-20464" />
                            </button>}
                        </div>}
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}
// Helper function to determine text color based on background color
function getContrastColor(hexColor) {
  // Convert hex to RGB
  let r = parseInt(hexColor.substr(1, 2), 16);
  let g = parseInt(hexColor.substr(3, 2), 16);
  let b = parseInt(hexColor.substr(5, 2), 16);
  // Calculate luminance
  let luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  // Return black or white depending on luminance
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}